import React, { useState, useEffect, useCallback } from 'react';
import { debounce } from 'lodash'; // Import lodash debounce
import Modal from './Modal'; // Assuming you have a custom Modal component
import { apiEndpoint } from '../constants/api';
import S3FileUpload from './form/S3FileUpload'; // Assuming you have a custom S3FileUpload component
import { Cloudinary } from '@cloudinary/url-gen';
import { notify } from './Toast';
import axios from 'axios';
import ImageUpload from './cloudinary/ImageUpload';

const labelStyles =
  'flex mb-2 text-lg font-medium text-gray-900 dark:text-white';
const inputStyles =
  'block w-full p-6 px-10 text-lg text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 mb-7';

const cld = new Cloudinary({
  cloud: {
    cloudName: 'prodz-com',
    uploadPreset: 'company_portfolio_images',
  },
});

const CompanyModal = ({
  isOpened,
  activeCompany,
  closeModal,
  updateActiveCompany,
  onLogoUploadComplete,
}) => {
  const [companyData, setCompanyData] = useState({
    company_name: '',
    description: '',
    location: '',
    facebook_url: '',
    twitter_url: '',
    linkedin_url: '',
    instagram_url: '',
  });

  const [isUpdating, setIsUpdating] = useState(false);
  const [initialCompanyData, setInitialCompanyData] = useState({});
  const { id: companyId } = activeCompany;

  const uploadPath = companyId
    ? apiEndpoint(`/admin/company/${companyId}/logo`)
    : null;

  // Create a debounced function for updateActiveCompany using lodash's debounce
  const debouncedUpdateActiveCompany = useCallback(
    debounce((updatedName) => {
      updateActiveCompany({ company_name: updatedName });
    }, 600),
    [updateActiveCompany] // The debounce function depends on the updateActiveCompany function
  );

  const handleChange = (e) => {
    const { id, value } = e.target;

    setCompanyData((prevData) => ({
      ...prevData,
      [id]: value,
    }));

    // If creating a new company and changing the company name, call the debounced function
    if (!companyId && id === 'company_name') {
      debouncedUpdateActiveCompany(value);
    }
  };

  // Check if any fields have changed
  const hasChanges = () => {
    return JSON.stringify(companyData) !== JSON.stringify(initialCompanyData);
  };

  // Synchronize the local state (companyName) with the company_name prop whenever it changes
  useEffect(() => {
    const initialData = {
      company_name: activeCompany.company_name || '',
      description: activeCompany.description || '',
      location: activeCompany.location || '',
      facebook_url: activeCompany.facebook_url || '',
      twitter_url: activeCompany.twitter_url || '',
      linkedin_url: activeCompany.linkedin_url || '',
      instagram_url: activeCompany.instagram_url || '',
    };

    setCompanyData(initialData);
    setInitialCompanyData(initialData);
  }, [activeCompany]);

  const handleImagesUpload = async (publicId) => {
    if (activeCompany.id) {
      try {
        const response = await axios.post(
          apiEndpoint(`/admin/company/${activeCompany.id}/portfolio-images`),
          { imagePublicId: publicId }
        );

        if (response.data.success) {
          notify('Image successfully uploaded!');
        } else {
          notify('Failed to save the image! Please try again');
        }
      } catch (error) {
        console.error('Failed to save the image! Please try again:', error);
        notify('Failed to save the image! Please try again');
      }
    } else {
      notify('Please create the company before uploading images.');
    }
  };

  const onUpdateClick = async () => {
    setIsUpdating(true);
    await updateActiveCompany(companyData);
    setIsUpdating(false);
    closeModal();
  };

  const onSubmit = (evt) => {
    evt.preventDefault();
  };

  return (
    <Modal
      title={`${companyId ? 'Update' : 'Create'} Company`}
      isOpened={isOpened}
      onClose={closeModal}
    >
      <div className="w-full">
        <div className="my-5">
          <form onSubmit={onSubmit}>
            {/* company name */}
            <label htmlFor="company_name" className={labelStyles}>
              Company Name
            </label>
            <input
              type="text"
              value={companyData.company_name}
              onChange={handleChange}
              id="company_name"
              className={inputStyles}
              placeholder="Company Name"
              required
            />

            {companyId && (
              <>
                {/* About Comapny */}
                <label htmlFor="description" className={labelStyles}>
                  Description
                </label>
                <textarea
                  className={inputStyles}
                  value={companyData.description}
                  onChange={handleChange}
                  id="description"
                  placeholder="About the company"
                />

                {/* Location */}
                <label htmlFor="location" className={labelStyles}>
                  Location
                </label>
                <input
                  type="text"
                  className={inputStyles}
                  value={companyData.location}
                  onChange={handleChange}
                  id="location"
                  placeholder="Company Location"
                />

                {/* Social Media Links */}
                <label htmlFor="facebook_url" className={labelStyles}>
                  Facebook
                </label>
                <input
                  className={inputStyles}
                  type="text"
                  value={companyData.facebook_url}
                  onChange={handleChange}
                  id="facebook_url"
                  placeholder="Facebook URL"
                />
                <label htmlFor="twitter_url" className={labelStyles}>
                  Twitter
                </label>
                <input
                  className={inputStyles}
                  type="text"
                  value={companyData.twitter_url}
                  onChange={handleChange}
                  id="twitter_url"
                  placeholder="Twitter URL"
                />
                <label htmlFor="linkedin_url" className={labelStyles}>
                  LinkedIn
                </label>
                <input
                  className={inputStyles}
                  type="text"
                  value={companyData.linkedin_url}
                  onChange={handleChange}
                  id="linkedin_url"
                  placeholder="LinkedIn URL"
                />
                <label htmlFor="instagram_url" className={labelStyles}>
                  Instagram
                </label>
                <input
                  className={inputStyles}
                  type="text"
                  value={companyData.instagram_url}
                  onChange={handleChange}
                  id="instagram_url"
                  placeholder="Instagram URL"
                />
                <div className="flex mt-5">
                  <button
                    type="button"
                    onClick={onUpdateClick}
                    disabled={!hasChanges() || isUpdating}
                    className={`px-4 py-2 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75  ${
                      !hasChanges() || isUpdating
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                  >
                    {isUpdating ? 'Updating...' : 'Update Company Details'}
                  </button>
                </div>
                <div className="flex mt-5">
                  <ImageUpload
                    title="Upload Portfolio Images"
                    cloudName={cld.cloudinaryConfig.cloud.cloudName}
                    uploadPreset={cld.cloudinaryConfig.cloud.uploadPreset}
                    onImageUpload={handleImagesUpload}
                  />
                </div>
              </>
            )}
          </form>
        </div>
        <div className="relative">
          {companyId ? (
            <S3FileUpload
              hideLabel={true}
              uploadPath={uploadPath}
              dragMessage="Drag & Drop your logo here or click to browse your files"
              fileType="Logo"
              onComplete={onLogoUploadComplete}
            />
          ) : null}
        </div>
      </div>
    </Modal>
  );
};

export default CompanyModal;
